import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Megaphone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import { useChat } from '@/contexts/ChatContext';
import { NotificationContext } from '@/contexts/NotificationContext';
import {
  ADMIN_ANNOUNCEMENT_EVENT,
  AdminAnnouncement,
  announcementHeading,
  enqueueAnnouncement,
  isAnnouncementExpired,
  isAnnouncementForUser,
  isInternalActionUrl,
  isSafeActionUrl,
  parseAnnouncementPayload,
  recordModalShown,
  shouldOpenModalForFrequency,
  shouldShowAnnouncement,
} from '@/lib/notifications/adminAnnouncement';
import {
  InAppSeverity,
  SEVERITY_PRESENTATION,
  inAppAnnouncementOf,
  isForCurrentSurface,
  isInAppAnnouncementExpired,
} from '@/lib/notifications/inAppNotification';

const SEVERITY_ICON: Record<InAppSeverity, React.ComponentType<{ className?: string }>> = {
  info: Megaphone,
  success: CheckCircle2,
  warning: AlertTriangle,
  critical: AlertTriangle,
};

export const AdminAnnouncementModal: React.FC = () => {
  const { user } = useAuth();
  const chat = useChat();
  const socket = chat?.socket ?? null;
  const isConnected = chat?.isConnected ?? false;
  const navigate = useNavigate();

  const [current, setCurrent] = useState<AdminAnnouncement | null>(null);
  const [queue, setQueue] = useState<AdminAnnouncement[]>([]);
  const seenIdsRef = useRef<Map<string, number>>(new Map());

  const notificationContext = useContext(NotificationContext);
  const notifications = notificationContext?.notifications;

  const replayedIdsRef = useRef<Set<string>>(new Set());

  const show = useCallback((announcement: AdminAnnouncement) => {
    recordModalShown(announcement.id, announcement.frequency);
    setCurrent((activeAnnouncement) => {
      if (activeAnnouncement) {
        setQueue((prev) => enqueueAnnouncement(prev, announcement));
        return activeAnnouncement;
      }
      return announcement;
    });
  }, []);

  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleAnnouncement = (payload: unknown) => {
      if (!isAnnouncementForUser(payload, userIdRef.current)) return;

      const announcement = parseAnnouncementPayload(payload);
      if (!announcement) return;

      if (!shouldShowAnnouncement(announcement.id, seenIdsRef.current, Date.now())) return;

      if (isAnnouncementExpired(announcement)) return;

      if (!shouldOpenModalForFrequency(announcement.id, announcement.frequency)) return;

      replayedIdsRef.current.add(announcement.id);
      show(announcement);
    };

    socket.on(ADMIN_ANNOUNCEMENT_EVENT, handleAnnouncement);
    return () => {
      socket.off(ADMIN_ANNOUNCEMENT_EVENT, handleAnnouncement);
    };
  }, [socket, isConnected, show]);

  useEffect(() => {
    if (!Array.isArray(notifications) || notifications.length === 0) return;

    for (const notification of notifications) {
      const inApp = inAppAnnouncementOf(notification);
      if (!inApp || !inApp.modalRequested) continue;
      if (!isForCurrentSurface(inApp)) continue;
      if (isInAppAnnouncementExpired(inApp)) continue;

      const id = inApp.announcementId;
      if (!id) continue;
      if (replayedIdsRef.current.has(id)) continue;
      if (!shouldOpenModalForFrequency(id, inApp.modalFrequency)) continue;

      replayedIdsRef.current.add(id);
      show({
        id,
        channel: 'in_app_notification',
        title: String((notification as { title?: unknown })?.title || ''),
        message: String((notification as { message?: unknown })?.message || ''),
        imageUrl: inApp.media.find((m) => m.type === 'image')?.url ?? null,
        actionUrl: inApp.actionUrl,
        sentAt: inApp.sentAt || '',
        sentByEmail: inApp.sentByEmail,
        severity: inApp.severity,
        expiresAt: inApp.expiresAt,
        frequency: inApp.modalFrequency,
      });
    }
  }, [notifications, show]);

  const dismiss = useCallback(() => {
    setQueue((prev) => {
      if (prev.length === 0) {
        setCurrent(null);
        return prev;
      }
      const [next, ...rest] = prev;
      setCurrent(next);
      return rest;
    });
  }, []);

  const handleAction = useCallback(() => {
    const url = current?.actionUrl;
    if (!isSafeActionUrl(url)) {
      dismiss();
      return;
    }
    const target = String(url);
    if (isInternalActionUrl(target)) {
      navigate(target);
    } else {
      window.open(target, '_blank', 'noopener,noreferrer');
    }
    dismiss();
  }, [current?.actionUrl, dismiss, navigate]);

  if (!current) return null;

  const showAction = isSafeActionUrl(current.actionUrl);
  const severity = SEVERITY_PRESENTATION[current.severity];
  const SeverityIcon = SEVERITY_ICON[current.severity];

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <DialogContent className="max-w-md" data-testid="admin-announcement-modal">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${severity.iconWrapClass}`}
              data-testid={`admin-announcement-severity-${current.severity}`}
            >
              <SeverityIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle
                className="text-left text-base leading-snug"
                data-testid="admin-announcement-title"
              >
                {announcementHeading(current)}
              </DialogTitle>
              {current.message ? (
                <DialogDescription
                  className="mt-1.5 whitespace-pre-wrap break-words text-left text-sm text-muted-foreground"
                  data-testid="admin-announcement-message"
                >
                  {current.message}
                </DialogDescription>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        {current.imageUrl ? (
          <img
            src={current.imageUrl}
            alt=""
            className="max-h-48 w-full rounded-md object-cover"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : null}

        <DialogFooter className="gap-2 sm:justify-end">
          {queue.length > 0 ? (
            <span
              className="mr-auto self-center text-xs text-muted-foreground"
              data-testid="admin-announcement-queue-count"
            >
              +{queue.length} more
            </span>
          ) : null}
          <Button variant={showAction ? 'outline' : 'default'} onClick={dismiss} data-testid="admin-announcement-dismiss">
            {queue.length > 0 ? 'Next' : 'Dismiss'}
          </Button>
          {showAction ? (
            <Button onClick={handleAction} data-testid="admin-announcement-action">
              View
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAnnouncementModal;
